import { createSelector } from 'reselect';
import { pageContext } from './page_context';
import { get } from './get';
import { executeTransform, executeSelector } from './transforms';

/**
 * Convenience function for determining the element type from and element schema
 * @param {Object} schema - The element schema
 * @returns {String} - The element type
 */
const getElType = (schema) => {
    if (schema.elType) {
        return schema.elType;
    }
    if (schema.field) {
        return schema.field.type;
    }
    return null; // keep eslint happy....
};

/**
 * Get the set of rule schemas for an element, including any default rules which are applied to
 * all elements of the same element type.
 * @param {Object} props - The element's set of props
 * @param {Object} formSchemaModel - The model for the formSchema. This can be passed in to make testing easier, if it is not passed in,
 * it will be retrieved from pageContext.
 * @returns {Array} - The set of all rule schemas for the element, including default rules
 */
const getElementRuleSchemas = (props, formSchemaModel = null) => {
    if (formSchemaModel === null) {
        // eslint-disable-next-line no-param-reassign
        formSchemaModel = pageContext.getProps().models.formSchemaModel;
    }
    let defaultRuleConfig = [];
    if (props.schema.stateKey && formSchemaModel.properties[props.schema.stateKey].defaultRules) {
        defaultRuleConfig = formSchemaModel.properties[props.schema.stateKey].defaultRules;
    }

    // Add the default rules that are specified in the formSchema for the elements elType, if any.
    const defaultRules = [];
    for (const ruleConfig of defaultRuleConfig) {
        // @todo: for now just add the default rules to all form components, eventually, we should have a way
        // of defining the container type for a collection of elements in the schema, so we know what sort of container
        // or element we are dealing with and so we can define
        // We currently only have one default rule defined for disabling form fields and we want this to apply at the
        // fieldrow level, so this should be ok for now. I haven't seen any adverse effects from this.
        defaultRules.push(ruleConfig);
    }

    let rules = [];
    if (props.schema.rules) {
        rules = defaultRules.concat(props.schema.rules);
    } else {
        rules = defaultRules;
    }
    return rules;
};

/**
 * Extract and return the Dynamic rule in a set of rules by filtering the rule name
 * @param {Array} ruleSchemas - The set of rules that may or may not contain the dynamic rule
 * @param {String} ruleName - The name of the dynamic rule
 * @returns {Object|null} - The dynamic rule in rules, or null if it is not in the schema.
 */
const getDynamicRuleByName = (ruleSchemas, ruleName) => {
    const ruleList = ruleSchemas.filter((ruleSchema) => ruleSchema.rule.indexOf(ruleName) >= 0);

    if (ruleList.length > 1) {
        throw Error(`More than one ${ruleName} rule was provided for an element`);
    }

    if (ruleList.length) {
        return ruleList[0];
    }

    return null;
};

/**
 * Apply any defined transforms to the filterValues for a rule
 * @param {Object} rule - A rule with an array of filterValues
 * @param {Object} state - The app state
 * @returns {Array} - The set of filterValues, with all transforms resolved.
 */
const getTransformedFilterValues = (rule, state) => {
    const values = [];
    for (const filterValue of rule.schema.filterValue) {
        if (typeof filterValue === 'string' && filterValue.startsWith('transform:')) {
            values.push(getTransformedValues(rule, state, filterValue));
        } else if (typeof filterValue === 'string' && filterValue.startsWith('selector:')) {
            values.push(getValuesFromSelector(rule, state, filterValue));
        } else {
            values.push(filterValue);
        }
    }
    return values;
};

/**
 * Get values for 'transform' based rules and run them through the specified transformer
 * eg: transform:age:vertical.quote.form.person.dob
 * will run vertical.quote.form.person.dob through the age transformer to get the users age
 * @param {Object} rule - A rule containing a transform
 * @param {Object} state - The app state
 * @param {String} stateKeyTransform - an override for when the transform isn't used in a stateKey
 * @returns {*} The result of the specified transform
 */
const getTransformedValues = (rule, state, stateKeyTransform = null) => {
    if (!stateKeyTransform) {
        // eslint-disable-next-line no-param-reassign
        stateKeyTransform = rule.schema.stateKey;
    }

    if (rule.schema.values) {
        const transformedValues = rule.schema.values.map((key) => state.getIn(key.split('.')));
        return executeTransform(stateKeyTransform, state, transformedValues);
    }

    return executeTransform(stateKeyTransform, state);
};

/**
 * Get values for 'selector' based rules and run them through the specified selector
 * eg: selector:noClaimDiscountToNumber:vertical.quote.form.coverDetail.driver.noClaimRating
 * will run vertical.quote.form.coverDetail.driver.noClaimRating through the noClaimDiscountToNumber selector to get a numerical value
 * @param {Object} rule - A rule containing a selector
 * @param {Object} state - The app state
 * @param {String} stateKeyTransform - an override for when the transform isn't used in a stateKey
 * @returns {*} The result of the specified transform
 */
const getValuesFromSelector = (rule, state, stateKeyTransform = null) => {
    if (!stateKeyTransform) {
        // eslint-disable-next-line no-param-reassign
        stateKeyTransform = rule.schema.stateKey;
    }

    if (rule.schema.values) {
        const transformedValues = rule.schema.values.map((key) => state.getIn(key.split('.')));
        return executeSelector(stateKeyTransform, state, transformedValues);
    }

    return executeSelector(stateKeyTransform, state);
};

/**
 * Get and return values for filterValues described by a stateKey
 * @param {Object} rule - The rule schema
 * @param {Object} state - The app state
 * @returns {*} - The state value
 */
const getValueForStateBasedFilterValue = (rule, state) => {
    // Get the expected values for rules that compare against a state value
    // This is separate from the if / else below as this is only for the expected value, the actual value
    // will still need to be extracted in the below if / else
    const stateKey = rule.schema.filterValue.stateKey;
    if (stateKey.startsWith('transform:')) {
        return getTransformedValues(rule, state, stateKey);
    } if (stateKey.startsWith('selector:')) {
        return getValuesFromSelector(rule, state, stateKey);
    }
    return state.getIn(stateKey.split('.'));
};

/**
 * Generator to return any possible stateKey / value mappings for ranges with stateKey based min / max values
 * @param {Object} rule - The rule schema
 * @param {Object} state - The app state
 * @todo: work out how to document the yield of a generator in esdoc and figure out the lint error on the func-style
 */
// eslint-disable-next-line func-style
function* getValuesForStateKeyBasedRanges(rule, state) {
    for (const filterRangeName of ['min', 'max', 'from']) {
        if (get(rule, `schema.filterValue.${filterRangeName}.stateKey`)) {
            const stateKey = rule.schema.filterValue[filterRangeName].stateKey;
            if (stateKey.startsWith('transform:')) {
                yield [stateKey, getTransformedValues(rule, state, stateKey)];
            } else if (stateKey.startsWith('selector:')) {
                yield [stateKey, getValuesFromSelector(rule, state, stateKey)];
            } else {
                yield [stateKey, state.getIn(stateKey.split('.'))];
            }
        }
    }
}

/**
 * Get the values from state that are required to evaluate the element rules
 * @param  {Object} state - The entire application state tree
 * @param  {Object} props - The props for the component that this is being used for
 * @param  {Object} rulesModel - The model for the rules. This can be passed in to make testing easier, if it is not passed in,
 * it will be retrieved from pageContext.
 * @param  {Object} formSchemaModel - The model for the formSchema. This can be passed in to make testing easier, if it is not passed in,
 * it will be retrieved from pageContext.
 * @returns {Object}       A map of element identifiers to state values
 */
const getElementRulesStateValues = (state, props, rulesModel = null, formSchemaModel = null) => {
    const elementRuleSchemas = getElementRuleSchemas(props, formSchemaModel);

    const values = {};
    if (elementRuleSchemas === undefined) {
        // Don't do anything else if the element doesn't have rules defined
        return values;
    }

    // Add the state value for the dynamic isSupplied rule if one is defined for this element
    const isSuppliedRule = getDynamicRuleByName(elementRuleSchemas, 'isSupplied');
    if (isSuppliedRule) {
        const { stateKey } = isSuppliedRule.props;
        values[stateKey] = state.getIn(stateKey.split('.'));
    }

    if (rulesModel === null) {
        // eslint-disable-next-line no-param-reassign
        rulesModel = pageContext.getProps().models.rulesModel;
    }

    // Get the current state values for all rules other than the isSupplied rule.
    for (const elementRuleSchema of elementRuleSchemas) {
        const rules = rulesModel.extractRules(elementRuleSchema.rule, elementRuleSchema.selectedRules);
        while (rules.length > 0) {
            const rule = rules.shift();
            // Don't get values for dynamic or compound rules dynamic rules are based off the current component,
            // compound rules are just collections of BaseRules
            if (rule.type === 'CompoundRule') {
                // Extract the BaseRules from the CompoundRule and add them to the list of rules to get values for
                for (const compoundBaseRule of rulesModel.extractRules(rule.schema.ruleExpr)) {
                    rules.push(compoundBaseRule);
                }
                continue;
            } else if (rule.type !== 'BaseRule') {
                // We've already handled Dynamic and CompoundRules, no need to do any more for them.
                continue;
            }

            // @todo: make from here down better

            if (get(rule, 'schema.filterValue.stateKey')) {
                // The filterValue needs to be extracted from state
                // See HnC rule: totalContentsCostMoreThanSpecifiedContentsCost
                values[rule.schema.filterValue.stateKey] = getValueForStateBasedFilterValue(rule, state);
            }

            const filterValue = get(rule, 'schema.filterValue', '');
            if (Array.isArray(filterValue)) {
                // The filterValue is an array that may contain transformed and selected values
                // See HnC rule: movedInPastTwoYears
                rule.schema.filterValue = getTransformedFilterValues(rule, state);
            }

            if (typeof filterValue === 'string' && filterValue.startsWith('transform:')) {
                // The filterValue is the result of a transform
                // See HnC rule: movedInCurrentYear
                rule.schema.filterValue = getTransformedValues(rule, state, rule.schema.filterValue);
            } else if (typeof filterValue === 'string' && filterValue.startsWith('selector:')) {
                // The filterValue is the result of a selector
                // We haven't had a case for this yet, but it's added to be consistent with transforms
                rule.schema.filterValue = getValuesFromSelector(rule, state, rule.schema.filterValue);
            }

            for (const [stateKey, stateValue] of getValuesForStateKeyBasedRanges(rule, state)) {
                // The filterValue is a range that needs to get it's min and / or max and / or from value from state
                // getValuesForStateKeyBasedRanges is a generator that yields each of the configured values
                // See HnC rule: isOldestPersonOlderThanPolicyHolder
                values[stateKey] = stateValue;
            }

            if (rule.schema.stateKey.startsWith('transform:')) {
                // The required state value is the result of a transform
                // See HnC rule: isOldestPersonOlderThanPolicyHolder
                values[rule.schema.stateKey] = getTransformedValues(rule, state);
            } else if (rule.schema.stateKey.startsWith('selector:')) {
                // The required state value is the result of a selector
                // We haven't had a case for this yet, but it's added to be consistent with transforms
                values[rule.schema.stateKey] = getValuesFromSelector(rule, state);
            } else if (rule.schema.stateKey.startsWith('props:')) {
                // In this case, the rule is not tied to a stateValue, but the value of one of the props on the component that the rule is being applied to
                // See Energy rule: disallowsOnlineApplication
                values[rule.schema.stateKey] = get(props.componentProps, rule.schema.stateKey.split(':')[1]);
            } else if (rule.schema.stateKey.startsWith('ruleProps:')) {
                // In this case the rule is using a dynamic stateKey, specified in it's elementRule props. We need to check that the key
                // exists and extract it from the elementRule props
                // See Energy rule: valueIsY
                const key = rule.schema.stateKey.split(':')[1];
                if (!get(elementRuleSchema, `props.${key}`)) {
                    throw Error(`A rule using ruleProps.${key} was specified, but this key is not available on the elementRule - ${JSON.stringify(elementRuleSchema)}`);
                }
                values[elementRuleSchema.props[key]] = get(state, elementRuleSchema.props[key]);
            } else {
                // The required state value is just a value extracted from state
                // See HnC rule: ownsHome
                const stateKey = rule.schema.stateKey;
                values[stateKey] = get(state, stateKey);
            }
        }
    }
    return values;
};

/**
 * Get the required props for the elementRules reselect
 * @param  {Object} state The entire application state tree
 * @param  {Object} props The props for the component that this is being used for
 * @param  {Object} rulesModel - The model for the rules. This can be passed in to make testing easier, if it is not passed in,
 * it will be retrieved from pageContext.
 * @param  {Object} formSchemaModel - The model for the formSchema. This can be passed in to make testing easier, if it is not passed in,
 * it will be retrieved from pageContext.
 * @returns {Object}       The props required for the reselect
 */
const getElementRulesProps = (state, props, rulesModel = null, formSchemaModel = null) => {
    // eslint-disable-next-line no-param-reassign
    formSchemaModel = formSchemaModel || pageContext.getProps().models.formSchemaModel;
    return {
        formSchemaModel,
        rulesModel: rulesModel || pageContext.getProps().models.rulesModel,
        elementRules: getElementRuleSchemas(props, formSchemaModel),
        identifier: props.schema.identifier
    };
};

/**
 * Reselect selector for element rules
 * @returns {Object} - The results of all rules for an element
 */
export const createAddElementRules = () => {
    return createSelector(
        [getElementRulesStateValues, getElementRulesProps], (values, ruleProps) => {
            if (ruleProps.elementRules === undefined) {
                return {};
            }
            const results = {};
            for (const ruleConfig of ruleProps.elementRules) {
                if (ruleProps.identifier) {
                    ruleConfig.elementId = ruleProps.identifier;
                    ruleConfig.id = `${ruleProps.identifier}.${ruleConfig.controls}`;
                } else {
                    // Required for rules that aren't tied to a specific element - eg transitioning to new page, modal visibility etc
                    ruleConfig.elementId = null; // don't need an elementID on these cos they don't represent an element
                    ruleConfig.id = `${ruleConfig.rule}.${ruleConfig.controls}`;
                }
                Object.assign(results, ruleProps.rulesModel.evaluateRuleExpr(ruleConfig, values));
            }
            return results;
        }
    );
};

/**
 * MapStateToProps closure for the element rules reselect selector
 * @param  {Object} rulesModel - The model for the rules. This can be passed in to make testing easier, if it is not passed in,
 * it will be retrieved from pageContext.
 * @param  {Object} formSchemaModel - The model for the formSchema. This can be passed in to make testing easier, if it is not passed in,
 * it will be retrieved from pageContext.
 * @returns {Function} - A function that can be called to return a mapStateToProps
 *                      function that returns the evaluated element rules
 */
export const elementRulesStateToProps = (rulesModel = null, formSchemaModel = null) => {
    const addElementRules = createAddElementRules();
    return (state, props) => {
        return {
            rules: addElementRules(state, props, rulesModel, formSchemaModel)
        };
    };
};

/**
 * Get the values from state that are required to evaluate all of the element rules for a page
 * @param  {Object} state - The entire application state tree
 * @param  {String} pageName - The name of the page to get the rules for
 * @param  {Object} rulesModel - The model for the rules. This can be passed in to make testing easier, if it is not passed in,
 * it will be retrieved from pageContext.
 * @param  {Object} formSchemaModel - The model for the formSchema. This can be passed in to make testing easier, if it is not passed in,
 * it will be retrieved from pageContext.
 * @returns {Object} values - A map of element identifiers to state values
 */
const getPageRulesStateValues = (state, pageName, rulesModel = null, formSchemaModel = null) => {
    // eslint-disable-next-line no-param-reassign
    formSchemaModel = formSchemaModel || pageContext.getProps().models.formSchemaModel;
    const values = {};
    const allElements = formSchemaModel.getPageElements(pageName).concat(formSchemaModel.getPageFieldSets(pageName));
    for (const element of allElements) {
        const newProps = {
            schema: element
        };
        Object.assign(values, getElementRulesStateValues(state, newProps, rulesModel, formSchemaModel));
    }
    return values;
};

/**
 * Reselect selector for page element rules
 * @param {String} pageName - The page to get the rules for
 * @param {String} ruleType - The type of rules to get, eg validation, visibility etc
 * @param  {Object} rulesModel - The model for the rules. This can be passed in to make testing easier, if it is not passed in,
 * it will be retrieved from pageContext.
 * @param  {Object} formSchemaModel - The model for the formSchema. This can be passed in to make testing easier, if it is not passed in,
 * it will be retrieved from pageContext.
 * @returns {Object} - The results of all rules for a page
 */
export const createAddPageRules = (pageName, ruleType, rulesModel = null, formSchemaModel = null) => {
    if (rulesModel === null) {
        // eslint-disable-next-line no-param-reassign
        rulesModel = pageContext.getProps().models.rulesModel;
    }
    if (formSchemaModel === null) {
        // eslint-disable-next-line no-param-reassign
        formSchemaModel = pageContext.getProps().models.formSchemaModel;
    }

    const allElements = formSchemaModel.getPageElements(pageName).concat(formSchemaModel.getPageFieldSets(pageName));
    return createSelector(
        [getPageRulesStateValues], (values) => {
            const results = {};
            for (const element of allElements) {
                if (!element.rules) {
                    continue;
                }
                for (const ruleConfig of element.rules) {
                    if (ruleConfig.controls === ruleType) {
                        ruleConfig.id = `${element.identifier}.${ruleConfig.controls}`;
                        ruleConfig.elementId = element.identifier;
                        results[element.identifier] = rulesModel.evaluateRuleExpr(ruleConfig, values);
                    }
                }
            }
            return results;
        }
    );
};

/**
 * MapStateToProps closure for the page rules reselect selector
 * @param {String} pageName - The page to get the rules for
 * @param {String} ruleType - The type of rules to get, eg validation, visibility etc
 * @param  {Object} rulesModel - The model for the rules. This can be passed in to make testing easier, if it is not passed in,
 * it will be retrieved from pageContext.
 * @param  {Object} formSchemaModel - The model for the formSchema. This can be passed in to make testing easier, if it is not passed in,
 * it will be retrieved from pageContext.
 * @returns {Function} - A function that can be called to return a mapStateToProps
 *                      function that returns the evaluated element rules for a page
 */
export const pageRulesStateToProps = (pageName, ruleType, rulesModel = null, formSchemaModel = null) => {
    if (!ruleType) {
        throw Error('A ruleType must be passed to pageRulesStateToProps');
    }
    const addPageRules = createAddPageRules(pageName, ruleType, rulesModel, formSchemaModel);
    return (state) => {
        return {
            rules: addPageRules(state, pageName, rulesModel, formSchemaModel)
        };
    };
};

/**
 * Get the Rules from the state given incoming rules as properties.
 * @param  {Object} state - The redux store
 * @param  {Object} props - The props passed to the current component.
 * @param  {Object} rulesModel - The model for the rules. This can be passed in to make testing easier, if it is not passed in,
 * it will be retrieved from pageContext.
 * @param  {Object} formSchemaModel - The model for the formSchema. This can be passed in to make testing easier, if it is not passed in,
 * it will be retrieved from pageContext.
 * @returns {Object} - The rules result from the state.
 */
export const getRulesFromState = (state, props, rulesModel = null, formSchemaModel = null) => {
    if (props.rules) {
        return elementRulesStateToProps(rulesModel, formSchemaModel)(state, {
            schema: {
                rules: props.rules
            },
            componentProps: props
        }).rules;
    }

    return {};
};

/**
 * Checks to see if the result of the rules have changed between nextProps and currentProps
 * @param {Object} nextProps - The incoming set of props
 * @param {Object} currentProps - The current set of props
 * @returns {Boolean} - True if the result of any of the rules have changed
 */
export const haveRulesChanged = (nextProps, currentProps) => {
    if (!currentProps.rules) {
        return false;
    }
    for (const controls of Object.keys(currentProps.rules)) {
        if (currentProps.rules[controls].result !== nextProps.rules[controls].result) {
            return true;
        }
    }
    return false;
};

export const testExports = {
    getElType,
    getElementRuleSchemas,
    getElementRulesProps,
    getDynamicRuleByName,
    getElementRulesStateValues,
    getPageRulesStateValues,
    getTransformedValues,
    getValueForStateBasedFilterValue,
    getValuesForStateKeyBasedRanges
};
